import {
  Directive,
  Input,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { IonButton } from '@ionic/angular';

@Directive({
  selector: '[test-key-container]',
  standalone: true
})
export class TestKeyContainerDirective implements AfterViewInit, OnDestroy {
  @Input('tkc-prepend') tkPrepend = '';
  @Input('tk-append') tkAppend = '';
  @Input('test-key-container') tkContainer = '';
  @Input('observeChanges') observeChanges = true;

  private observer: MutationObserver | null = null;
  private debounceTimer: any | null = null;
  private readonly debounceDelay = 500; // milliseconds

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.updateDescendantElements();

    if (this.observeChanges) {
      this.observeDomChanges();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private observeDomChanges(): void {
    this.observer = new MutationObserver((mutations: MutationRecord[]) => {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.updateDescendantElements();
      }, this.debounceDelay);
    });

    this.observer.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  private updateDescendantElements(): void {
    const hostElement = this.elementRef.nativeElement;
    const elements = hostElement.querySelectorAll(
      'a, button, [t-key], input, [test-key], ion-button, ion-input, ion-select, ion-textarea, ion-item'
    ) as NodeListOf<HTMLElement>;

    elements.forEach((element: any) => {
      let keyValue = '';

      if (element.hasAttribute('test-key')) {
        keyValue = element.getAttribute('test-key') || '';
      }

      if (!keyValue) {
        if (element.id) {
          keyValue = element.id;
        } else if (element.hasAttribute('name')) {
          keyValue = element.getAttribute('name') || '';
        }

        if (!keyValue && element.hasAttribute('aria-label')) {
          keyValue = element.getAttribute('aria-label') || '';
        }

        if (!keyValue && element.hasAttribute('title')) {
          keyValue = element.getAttribute('title') || '';
        }

        if (!keyValue) {
          keyValue = 'unknown';
        }
      }

      if (keyValue) {
        if (keyValue === 'unknown' && element.innerText.trim()) {
          keyValue = element.innerText.trim();
        }

        if (keyValue === 'unknown' ) {
          keyValue = element.value || 'unknown';
        }

       

        let isInput = element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement || element.tagName.toLowerCase() === 'ion-input' || element.tagName.toLowerCase() === 'ion-select' || element.tagName.toLowerCase() === 'ion-textarea';
        let isButton = element.getAttribute('ng-reflect-button') !== null || element['type'] === "button" || element instanceof HTMLButtonElement || element.tagName.toLowerCase() === 'ion-button' || element instanceof IonButton;
        let isLink = element.getAttribute('ng-reflect-href') !== null || element instanceof HTMLAnchorElement;

        if(isLink) {
          keyValue += '-link';
        }

        if(isButton)
        {
          let isDropdown = element.getAttribute('data-ion-popover-trigger') !== null || element.getAttribute('data-ion-action-sheet-trigger') !== null;
          keyValue += `${isDropdown ? '-dropdown' : ''}-btn`;
        }

        if(isInput && !isButton)
        {
          keyValue += '-input';
        }

        // Remove whitespace and special characters
        keyValue = keyValue.replace(/[^a-zA-Z0-9_-]/g, '');

        // Prepend and append values
        if (this.tkPrepend) {
          keyValue = `${this.tkPrepend}-${keyValue}`;
        }

        if (element.hasAttribute('test-key-append')) {
          keyValue += `-${element.getAttribute('test-key-append')}`;
        }

        element.setAttribute('data-test-key', keyValue);
        this.removeTestKeyAttributes(element);
      }
    });

    if (this.tkContainer) {
      hostElement.setAttribute('data-test-key', this.tkContainer);
    }
  }

  private removeTestKeyAttributes(element: HTMLElement): void {
    const attributesToRemove = [
      'test-key',
      't-key',
      'tk-append',
      'tkc-prepend',
      'test-key-append',
      'test-key-prepend',
    ];
    attributesToRemove.forEach((attr) => {
      if (element.hasAttribute(attr)) {
        element.removeAttribute(attr);
      }
    });
  }
}